import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Badge,
  Alert,
  ListGroup
} from 'react-bootstrap';
import { FaHistory } from 'react-icons/fa';
import ObjectConfigApi from '../../api/ObjectConfigApi';

const FieldHistoryFieldMapping = ({
  show,
  onHide,
  fields = [],
  objectName,
  objectConfigId,
  onSave
}) => {
  const [selectedHistoryFields, setSelectedHistoryFields] = useState([]);
  const [initialHistoryFields, setInitialHistoryFields] = useState([]);
  const [fieldHistoryEnabled, setFieldHistoryEnabled] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load existing field history configuration when modal opens
  useEffect(() => {
    if (show && objectConfigId) {
      loadFieldHistoryConfiguration();
    }
  }, [show, objectConfigId]);

  const loadFieldHistoryConfiguration = async () => {
    try {
      setLoading(true);
      console.log('Loading field history configuration for:', objectConfigId);

      const response = await ObjectConfigApi.getFieldHistoryMapping(objectConfigId);

      if (response.success && response.data && response.data.length > 0) {
        const fieldIds = response.data.map(item => item.field_config_id);
        setSelectedHistoryFields(fieldIds);
        setInitialHistoryFields(fieldIds);
        setFieldHistoryEnabled(true);
        console.log('Loaded existing field history mappings:', fieldIds);
      } else {
        setSelectedHistoryFields([]);
        setInitialHistoryFields([]);
        setFieldHistoryEnabled(false);
        console.log('No existing field history mappings found');
      }
    } catch (error) {
      console.error('Error loading field history configuration:', error);
      setSelectedHistoryFields([]);
      setInitialHistoryFields([]);
      setFieldHistoryEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleHistoryField = (fieldId) => {
    setSelectedHistoryFields(prev => {
      if (prev.includes(fieldId)) {
        // Remove if already selected
        return prev.filter(id => id !== fieldId);
      } else {
        // Add if under limit
        if (prev.length < 30) {
          return [...prev, fieldId];
        } else {
          // Show warning via parent component
          if (onSave) {
            onSave({
              success: false,
              message: 'Maximum 30 fields can be tracked for field history',
              variant: 'warning'
            });
          }
          return prev;
        }
      }
    });
  };

  const handleEnableFieldHistory = async () => {
    if (selectedHistoryFields.length === 0) {
      if (onSave) {
        onSave({
          success: false,
          message: 'Please select at least one field to track',
          variant: 'warning'
        });
      }
      return;
    }

    try {
      setSaving(true);

      // Determine which fields are new and which are deleted
      const newFields = selectedHistoryFields.filter(
        fieldId => !initialHistoryFields.includes(fieldId)
      );
      const deletedFields = initialHistoryFields.filter(
        fieldId => !selectedHistoryFields.includes(fieldId)
      );

      console.log('Saving field history mapping:');
      console.log('Object Config ID:', objectConfigId);
      console.log('New fields:', newFields);
      console.log('Deleted fields:', deletedFields);

      const response = await ObjectConfigApi.saveFieldHistoryMapping({
        objectConfigId,
        newFields,
        deletedFields
      });

      if (response.success) {
        setFieldHistoryEnabled(true);
        setInitialHistoryFields(selectedHistoryFields);

        // Build success message
        let successMessage = `Field history ${fieldHistoryEnabled ? 'updated' : 'enabled'} successfully for ${selectedHistoryFields.length} field(s)`;

        // Add info about history object creation if applicable
        if (response.data && response.data.historyObjectCreated) {
          successMessage += `. History object '${objectName}_history' has been created.`;
        }

        if (onSave) {
          onSave({
            success: true,
            message: successMessage,
            variant: 'success',
            enabled: true,
            selectedFields: selectedHistoryFields,
            historyObjectCreated: response.data?.historyObjectCreated || false
          });
        }
        onHide();
      } else {
        if (onSave) {
          onSave({
            success: false,
            message: response.message || 'Failed to save field history configuration',
            variant: 'danger'
          });
        }
      }
    } catch (error) {
      console.error('Error saving field history:', error);
      if (onSave) {
        onSave({
          success: false,
          message: 'Failed to save field history configuration',
          variant: 'danger'
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSearchFilter(''); // Clear search when closing
    onHide();
  };

  const filteredFields = fields.filter((f) => {
    // Exclude system fields, readonly fields, calculated fields, and rollup summary fields
    const dataType = (f.data_type || '').toLowerCase();

    // Exclude non-trackable field types
    if (f.is_readonly || dataType === 'calculated' || dataType === 'rollupsummary') {
      return false;
    }

    // Apply search filter
    if (!searchFilter.trim()) return true;
    const q = searchFilter.toLowerCase();
    return (
      (f.field_label || '').toLowerCase().includes(q) ||
      (f.field_name || '').toLowerCase().includes(q) ||
      dataType.includes(q)
    );
  });

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaHistory className="me-2 text-primary" />
          {fieldHistoryEnabled ? 'Edit Field History' : 'Enable Field History'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <small>
            <strong>Note:</strong> Select up to 30 fields to track their history.
            Once enabled, you can edit field selections but cannot disable field history tracking.
            <br />
            <strong>Excluded:</strong> System fields, readonly fields, calculated fields, and rollup summary fields cannot be tracked.
          </small>
        </Alert>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading field history configuration...</p>
          </div>
        ) : (
          <>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Select Fields to Track</h6>
              <Badge bg={selectedHistoryFields.length >= 30 ? 'danger' : 'primary'}>
                {selectedHistoryFields.length} / 30 selected
              </Badge>
            </div>

            <Form.Control
              type="text"
              placeholder="Search fields..."
              className="mb-3"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {fields.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <p>No fields available</p>
                </div>
              ) : filteredFields.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <p>No fields match your search</p>
                </div>
              ) : (
                <ListGroup>
                  {filteredFields.map((field) => (
                    <ListGroup.Item
                      key={field.id}
                      className="d-flex align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleToggleHistoryField(field.id)}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={selectedHistoryFields.includes(field.id)}
                        onChange={() => handleToggleHistoryField(field.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="me-3"
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{field.field_label}</strong>
                            <br />
                            <small className="text-muted">
                              <code>{field.field_name}</code> - {field.data_type?.toUpperCase()}
                            </small>
                          </div>
                          {field.is_required && (
                            <Badge bg="danger" className="ms-2">Required</Badge>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleEnableFieldHistory}
          disabled={selectedHistoryFields.length === 0 || saving || loading}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            fieldHistoryEnabled ? 'Update Field History' : 'Enable Field History'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FieldHistoryFieldMapping;
